import {
  clampRectInside,
  constrainRectToRatioInside,
  integerRectInside,
  largestRectForRatioInside,
} from '@/core/geometry'
import type { AspectRatio, Point, Rect } from '@/core/geometry/types'

export const MIN_CROP_SHORT_SIDE = 64

export interface CropZoom {
  value: number
  max: number
}

function localRect(rect: Rect, bounds: Rect): Rect {
  return { ...rect, x: rect.x - bounds.x, y: rect.y - bounds.y }
}

function globalRect(rect: Rect, bounds: Rect): Rect {
  return { ...rect, x: rect.x + bounds.x, y: rect.y + bounds.y }
}

export function panFrame(
  rect: Rect,
  screenDelta: Point,
  screenScale: number,
  bounds: Rect,
): Rect {
  if (!Number.isFinite(screenScale) || screenScale <= 0) {
    throw new Error('Crop pan requires a finite positive screen scale')
  }
  const region = integerRectInside(bounds)
  const current = localRect(rect, region)
  return globalRect(clampRectInside({
    ...current,
    x: current.x - screenDelta.x / screenScale,
    y: current.y - screenDelta.y / screenScale,
  }, region), region)
}

export function cropZoom(rect: Rect, bounds: Rect, ratio: AspectRatio): CropZoom {
  const largest = largestRectForRatioInside(bounds, ratio)
  const effectiveMinimumSide = Math.min(MIN_CROP_SHORT_SIDE, largest.width, largest.height)
  const max = Math.max(1, Math.min(largest.width, largest.height) / effectiveMinimumSide)
  const value = Math.min(max, Math.max(1, Math.max(
    largest.width / rect.width,
    largest.height / rect.height,
  )))
  return { value, max }
}

export function cropRectForZoom(
  rect: Rect,
  zoom: number,
  bounds: Rect,
  ratio: AspectRatio,
): Rect {
  if (!Number.isFinite(zoom) || zoom <= 0) {
    throw new Error('Crop zoom must be finite and positive')
  }
  const range = cropZoom(rect, bounds, ratio)
  const nextZoom = Math.min(range.max, Math.max(1, zoom))
  const largest = largestRectForRatioInside(bounds, ratio)
  return constrainRectToRatioInside({
    x: rect.x + rect.width / 2 - largest.width / nextZoom / 2,
    y: rect.y + rect.height / 2 - largest.height / nextZoom / 2,
    width: largest.width / nextZoom,
    height: largest.height / nextZoom,
  }, bounds, ratio)
}

export function zoomFrame(
  rect: Rect,
  factor: number,
  bounds: Rect,
  ratio: AspectRatio,
): Rect {
  if (!Number.isFinite(factor) || factor <= 0) {
    throw new Error('Crop zoom factor must be finite and positive')
  }
  return cropRectForZoom(rect, cropZoom(rect, bounds, ratio).value * factor, bounds, ratio)
}

export function zoomFrameAroundPoint(
  rect: Rect,
  factor: number,
  screenOffsetFromCenter: Point,
  screenScale: number,
  bounds: Rect,
  ratio: AspectRatio,
): Rect {
  if (!Number.isFinite(factor) || factor <= 0) {
    throw new Error('Crop focal zoom factor must be finite and positive')
  }
  if (!Number.isFinite(screenScale) || screenScale <= 0) {
    throw new Error('Crop focal zoom requires a finite positive screen scale')
  }
  if (!Number.isFinite(screenOffsetFromCenter.x) || !Number.isFinite(screenOffsetFromCenter.y)) {
    throw new Error('Crop focal zoom requires a finite screen point')
  }
  const range = cropZoom(rect, bounds, ratio)
  const nextZoom = Math.min(range.max, Math.max(1, range.value * factor))
  const largest = largestRectForRatioInside(bounds, ratio)
  const nextWidth = largest.width / nextZoom
  const nextHeight = largest.height / nextZoom
  const nextScreenScale = screenScale * rect.width / nextWidth
  const sourcePoint = {
    x: rect.x + rect.width / 2 + screenOffsetFromCenter.x / screenScale,
    y: rect.y + rect.height / 2 + screenOffsetFromCenter.y / screenScale,
  }
  return constrainRectToRatioInside({
    x: sourcePoint.x - screenOffsetFromCenter.x / nextScreenScale - nextWidth / 2,
    y: sourcePoint.y - screenOffsetFromCenter.y / nextScreenScale - nextHeight / 2,
    width: nextWidth,
    height: nextHeight,
  }, bounds, ratio)
}
